'use client';

import { c as _c } from "react/compiler-runtime";
import type { Data } from '@repo/strapi-types';
import { useTranslations } from 'next-intl';
import type React from 'react';
import { useRef } from 'react';
import { useIsomorphicLayoutEffect } from 'usehooks-ts';
import { Tag } from '@/components/elementary/Tag';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SITE_HEADER_OFFSET_MOBILE, STICKY_HEADER_BOTTOM_THRESHOLD } from '@/constants/pricing.constants';
import { useRefSticky } from '@/hooks/useRefSticky';
import { formatEventName, useAnalytics } from '@/lib/analytics/analytics';
import { ANALYTICS_EVENTS } from '@/lib/analytics/analytics.constants';
import { cn } from '@/lib/styles';
import { PRICING_TEST_IDS } from '@/lib/test-ids';
import type { PricingItem } from '@/types/api/pricing.types';
export function PlanTabs(t0) {
  const $ = _c(28);
  const {
    data,
    currentTab,
    setCurrentTab,
    renderContent,
    className
  } = t0;
  const t = useTranslations("Pricing");
  const {
    track
  } = useAnalytics();
  const stickyRef = useRef(null);
  const tabScrollRef = useRef(null);
  const tabRefs = useRef(null);
  const {
    isSticky
  } = useRefSticky(stickyRef, SITE_HEADER_OFFSET_MOBILE, STICKY_HEADER_BOTTOM_THRESHOLD);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = function getTabMap() {
      if (!tabRefs.current) {
        tabRefs.current = new Map();
      }
      return tabRefs.current;
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const getTabMap = t1;
  let t2;
  let t3;
  if ($[1] !== currentTab) {
    t2 = () => {
      const container = tabScrollRef.current;
      const active = tabRefs.current?.get(currentTab);
      if (!container || !active) {
        return;
      }
      container.scrollLeft = active.offsetLeft - (container.clientWidth - active.offsetWidth) / 2;
    };
    t3 = [currentTab];
    $[1] = currentTab;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useIsomorphicLayoutEffect(t2, t3);
  let t4;
  if ($[4] !== setCurrentTab || $[5] !== track) {
    t4 = value => {
      track({
        eventType: formatEventName(ANALYTICS_EVENTS.PRICING_PLAN_TAB_CLICKED),
        eventProperties: {
          planName: value
        }
      });
      setCurrentTab(value);
    };
    $[4] = setCurrentTab;
    $[5] = track;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const handleTabChange = t4;
  const t5 = isSticky ? "pt-3 border-neutral-light-100 transition-colors duration-150 ease-in-out" : "border-transparent";
  let t6;
  if ($[7] !== t5) {
    t6 = cn("sticky flex justify-center w-auto px-0 -mx-6  z-20 top-(--site-header-offset-mobile) pb-0 h-auto rounded-none bg-brand-black border-b", t5);
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== data || $[10] !== t) {
    t7 = data?.map(item => <TabsTrigger value={String(item?.title)} key={`${item?.id}-tab`} ref={node => {
      const map = getTabMap();
      const title = String(item?.title);
      map.set(title, node);
      return () => {
        map.delete(title);
      };
    }} data-test-id={PRICING_TEST_IDS.CONTENT_TAB_TRIGGER} className="group/tabs-trigger px-5 py-3 text-neutral-light-100 data-[state=active]:bg-neutral-light-100 data-[state=active]:text-brand-black transition-colors duration-200 ease-in-out"><span className="lok-text-body-bold-medium">{item?.title}</span>{item?.mostPopular && <Tag name={t("mostPopular")} size="small" className="w-fit text-sm lg:text-sm ml-1.25 -mr-1.25 group-data-[state=active]/tabs-trigger:text-neutral-light-100 group-data-[state=active]/tabs-trigger:bg-brand-black transition-colors duration-200 ease-in-out" fontWeight="font-bold" active={true} customColors={{
        backgroundColor: "neutral-light-100",
        borderColor: "neutral-light-100",
        textColor: "brand-black"
      }} />}</TabsTrigger>);
    $[9] = data;
    $[10] = t;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t7) {
    t8 = <div ref={tabScrollRef} className="flex overflow-auto px-6 pb-3 w-auto no-scrollbar">{t7}</div>;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t6 || $[15] !== t8) {
    t9 = <TabsList className={t6} ref={stickyRef}>{t8}</TabsList>;
    $[14] = t6;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== data || $[18] !== renderContent) {
    t10 = data?.map(item_0 => <TabsContent forceMount={true} className="w-full col-start-1 row-start-1 data-[state=inactive]:invisible" value={String(item_0?.title)} key={`${item_0?.id}-content`} data-test-id={PRICING_TEST_IDS.CONTENT_TAB_CONTENT}>{renderContent ? renderContent(item_0) : null}</TabsContent>);
    $[17] = data;
    $[18] = renderContent;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10) {
    t11 = <div className="grid">{t10}</div>;
    $[20] = t10;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== className || $[23] !== currentTab || $[24] !== handleTabChange || $[25] !== t11 || $[26] !== t9) {
    t12 = <Tabs data-test-id={PRICING_TEST_IDS.CONTENT_TAB} value={currentTab} onValueChange={handleTabChange} className={className}>{t9}{t11}</Tabs>;
    $[22] = className;
    $[23] = currentTab;
    $[24] = handleTabChange;
    $[25] = t11;
    $[26] = t9;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  return t12;
}